import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Star, Users, Bell, Search, ExternalLink, Trash2, LogOut,
  Film, Trophy, UserPlus, Copy, GraduationCap, MapPin
} from 'lucide-react';
import { API } from '../lib/constants';

const StatPill = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1 text-white/55 text-xs"> {icon} {label} </span>
);

const RecruitCard = ({ r, onStar, onRemove, onView }) => {
  const initials = (r.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 flex gap-4" data-testid={`recruit-card-${r.user_id}`}>
      {r.profile_picture_url ? (
        <img src={r.profile_picture_url} alt={r.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-[#16A34A]/20 flex items-center justify-center text-lg font-black text-[#4ade80] flex-shrink-0">{initials}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-black truncate">{r.name}</h3>
              {r.is_live ? (
                <span className="text-[10px] font-bold bg-[#16A34A]/20 text-[#4ade80] px-1.5 py-0.5 rounded-full">LIVE</span>
              ) : (
                <span className="text-[10px] font-bold bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded-full">PENDING</span>
              )}
              {r.source === 'submission' && <span className="text-[10px] font-bold bg-white/10 text-white/60 px-1.5 py-0.5 rounded-full">SUBMITTED</span>}
            </div>
            <p className="text-white/40 text-xs truncate">
              {(r.positions || []).join(' / ') || '—'}{r.class_year ? ` · '${String(r.class_year).slice(-2)}` : ''}
            </p>
          </div>
          <button
            onClick={() => onStar(r)}
            data-testid={`star-btn-${r.user_id}`}
            title={r.is_star ? 'Unstar' : 'Star this recruit'}
            className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${r.is_star ? 'text-amber-400' : 'text-white/25 hover:text-white/60'}`}
          >
            <Star className="h-5 w-5" fill={r.is_star ? 'currentColor' : 'none'} />
          </button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
          {r.school && <StatPill icon={<GraduationCap className="h-3.5 w-3.5" />} label={r.school} />}
          {(r.city || r.state) && <StatPill icon={<MapPin className="h-3.5 w-3.5" />} label={[r.city, r.state].filter(Boolean).join(', ')} />}
          <StatPill icon={<Film className="h-3.5 w-3.5" />} label={`${r.highlight_count} film`} />
          <StatPill icon={<Trophy className="h-3.5 w-3.5" />} label={`${r.offers_count} offers`} />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => onView(r)} data-testid={`view-recruit-${r.user_id}`} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
            <ExternalLink className="h-3.5 w-3.5" /> View profile
          </button>
          <button onClick={() => onRemove(r)} data-testid={`remove-recruit-${r.user_id}`} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const CoachDashboard = () => {
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('recruits'); // recruits | feed
  const [recruits, setRecruits] = useState([]);
  const [feed, setFeed] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/coach/me`, { withCredentials: true });
      setCoach(res.data.coach);
    } catch {
      toast.error('Please sign in as a coach');
      navigate('/signin');
    }
  }, [navigate]);

  const loadRecruits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/coach/recruits`, { withCredentials: true });
      setRecruits(res.data.recruits || []);
    } catch { /* handled by loadMe */ }
  }, []);

  const loadFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/coach/feed`, { withCredentials: true });
      setFeed(res.data.events || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadMe();
      await Promise.all([loadRecruits(), loadFeed()]);
      setLoading(false);
    })();
  }, [loadMe, loadRecruits, loadFeed]);

  // Search (debounced)
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/coach/search`, { params: { q: query }, withCredentials: true });
        setResults(res.data.results || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  const openFeed = async () => {
    setTab('feed');
    try {
      await axios.post(`${API}/api/coach/feed/seen`, {}, { withCredentials: true });
      setCoach(c => c ? { ...c, unseen_count: 0 } : c);
    } catch { /* noop */ }
  };

  const toggleStar = async (r) => {
    const next = !r.is_star;
    setRecruits(list => list.map(x => x.user_id === r.user_id ? { ...x, is_star: next } : x));
    try { await axios.post(`${API}/api/coach/star/${r.user_id}`, { is_star: next }, { withCredentials: true }); }
    catch { setRecruits(list => list.map(x => x.user_id === r.user_id ? { ...x, is_star: !next } : x)); toast.error('Could not update star'); }
  };

  const removeRecruit = async (r) => {
    setRecruits(list => list.filter(x => x.user_id !== r.user_id));
    try {
      await axios.delete(`${API}/api/coach/follow/${r.user_id}`, { withCredentials: true });
      setResults(res => res.map(x => x.user_id === r.user_id ? { ...x, already_following: false } : x));
    } catch { toast.error('Could not remove'); loadRecruits(); }
  };

  const followPlayer = async (p) => {
    setResults(res => res.map(x => x.user_id === p.user_id ? { ...x, already_following: true } : x));
    try {
      await axios.post(`${API}/api/coach/follow`, { player_user_id: p.user_id, source: 'search' }, { withCredentials: true });
      toast.success(`Following ${p.name}`);
      loadRecruits();
    } catch { toast.error('Could not follow'); setResults(res => res.map(x => x.user_id === p.user_id ? { ...x, already_following: false } : x)); }
  };

  const viewProfile = (r) => window.open(`/${r.username}`, '_blank');

  const copyPageLink = () => {
    const link = `${window.location.origin}/coach/${coach.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Public page link copied');
  };

  const signOut = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
    localStorage.clear();
    navigate('/signin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="coach-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#16A34A]" />
      </div>
    );
  }
  if (!coach) return null;

  const stars = recruits.filter(r => r.is_star);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="coach-dashboard">
      {/* Header */}
      <div className="border-b border-white/10 sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/offersz-o.png" alt="Offersz" className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-black leading-tight truncate" data-testid="coach-dash-name">{coach.display_name}</p>
              <p className="text-white/40 text-xs truncate">{coach.team_name || 'Recruiting board'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyPageLink} data-testid="copy-page-link" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-3 py-2 rounded-full text-sm flex items-center gap-1.5">
              <Copy className="h-4 w-4" /> <span className="hidden sm:inline">My page</span>
            </button>
            <button onClick={signOut} data-testid="coach-signout" className="text-white/40 hover:text-white p-2"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('recruits')} data-testid="tab-recruits"
            className={`px-4 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 transition-colors ${tab === 'recruits' ? 'bg-[#16A34A] text-white' : 'bg-white/5 text-white/50 hover:text-white'}`}>
            <Users className="h-4 w-4" /> My Recruits <span className="opacity-70">({recruits.length})</span>
          </button>
          <button onClick={openFeed} data-testid="tab-feed"
            className={`px-4 py-2.5 rounded-full text-sm font-bold flex items-center gap-2 transition-colors relative ${tab === 'feed' ? 'bg-[#16A34A] text-white' : 'bg-white/5 text-white/50 hover:text-white'}`}>
            <Bell className="h-4 w-4" /> Updates
            {coach.unseen_count > 0 && <span data-testid="feed-unseen-badge" className="ml-0.5 bg-amber-500 text-black text-[10px] font-black px-1.5 rounded-full">{coach.unseen_count}</span>}
          </button>
        </div>

        {tab === 'recruits' && (
          <div data-testid="recruits-panel">
            {/* Add recruits search */}
            <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 mb-6">
              <label className="text-sm font-semibold text-white/70 flex items-center gap-2 mb-2"><UserPlus className="h-4 w-4" /> Add a recruit already on Offersz</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, school, or @username"
                  data-testid="coach-search-input"
                  className="w-full bg-white/5 border border-white/10 text-white h-11 rounded-xl pl-10 pr-4 outline-none focus:border-[#16A34A] transition-colors" />
              </div>
              {query.trim().length >= 2 && (
                <div className="mt-3 space-y-2" data-testid="coach-search-results">
                  {searching ? (
                    <p className="text-white/40 text-sm py-2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>
                  ) : results.length === 0 ? (
                    <p className="text-white/40 text-sm py-2">No players found.</p>
                  ) : results.map(p => (
                    <div key={p.user_id} className="flex items-center gap-3 bg-white/[0.03] rounded-xl p-2.5" data-testid={`search-result-${p.user_id}`}>
                      {p.profile_picture_url ? <img src={p.profile_picture_url} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-[#16A34A]/20" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{p.name}</p>
                        <p className="text-white/40 text-xs truncate">{[p.school, (p.positions || []).join('/')].filter(Boolean).join(' · ')}</p>
                      </div>
                      {p.already_following ? (
                        <span className="text-white/40 text-xs font-bold px-3">Following</span>
                      ) : (
                        <button onClick={() => followPlayer(p)} data-testid={`follow-btn-${p.user_id}`} className="bg-[#16A34A] hover:bg-[#0F5132] text-white text-xs font-bold px-3 py-1.5 rounded-full">Follow</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {recruits.length === 0 ? (
              <div className="border border-dashed border-white/15 rounded-2xl py-16 text-center text-white/40" data-testid="recruits-empty">
                <Users className="h-10 w-10 mx-auto mb-3 text-white/20" />
                <p className="font-semibold text-white/60">No recruits yet</p>
                <p className="text-sm mt-1">Share your page link so players can drop their film — or search above.</p>
              </div>
            ) : (
              <>
                {stars.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-sm font-black text-amber-400 flex items-center gap-1.5 mb-3"><Star className="h-4 w-4" fill="currentColor" /> STARRED</h2>
                    <div className="grid md:grid-cols-2 gap-3">
                      {stars.map(r => <RecruitCard key={r.user_id} r={r} onStar={toggleStar} onRemove={removeRecruit} onView={viewProfile} />)}
                    </div>
                  </div>
                )}
                <h2 className="text-sm font-black text-white/40 mb-3">ALL RECRUITS</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {recruits.filter(r => !r.is_star).map(r => <RecruitCard key={r.user_id} r={r} onStar={toggleStar} onRemove={removeRecruit} onView={viewProfile} />)}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'feed' && (
          <div data-testid="feed-panel">
            {feed.length === 0 ? (
              <div className="border border-dashed border-white/15 rounded-2xl py-16 text-center text-white/40" data-testid="feed-empty">
                <Bell className="h-10 w-10 mx-auto mb-3 text-white/20" />
                <p className="font-semibold text-white/60">No updates yet</p>
                <p className="text-sm mt-1">When your recruits add film or land an offer, it shows up here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {feed.map((e, i) => (
                  <div key={i} className={`flex items-center gap-3 rounded-2xl p-3 border ${e.is_new ? 'bg-[#16A34A]/[0.07] border-[#16A34A]/25' : 'bg-white/[0.03] border-white/10'}`} data-testid={`feed-event-${i}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${e.type === 'offer' ? 'bg-amber-500/15 text-amber-300' : 'bg-[#16A34A]/20 text-[#4ade80]'}`}>
                      {e.type === 'offer' ? <Trophy className="h-5 w-5" /> : <Film className="h-5 w-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <button onClick={() => e.username && window.open(`/${e.username}`, '_blank')} className="font-bold hover:underline">{e.player_name || 'A recruit'}</button>
                        <span className="text-white/50"> {e.type === 'offer' ? 'added an offer' : 'added a highlight'}</span>
                      </p>
                      <p className="text-white/40 text-xs truncate">{e.title}</p>
                    </div>
                    {e.is_new && <span className="text-[10px] font-black bg-[#16A34A] text-white px-2 py-0.5 rounded-full flex-shrink-0">NEW</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachDashboard;
